import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import StarIcon from "@mui/icons-material/StarBorder";
import Typography from "@mui/material/Typography";
import {
  CUSTOM,
  BUTTON_CONTACT_US,
  VALUE_SUBSCRIPTION_MONTHLY,
  VALUE_SUBSCRIPTION_YEARLY_UNIQUE_PAYMENT,
  VALUE_SUBSCRIPTION_PRORATED,
  VALUE_SUBSCRIPTION_AFTER_PRORATED,
  MOST_POPULAR,
  BUTTON_SUBSCRIBE_NOW,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISER,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE_CUSTOM,
  BUTTON_UPGRADE_PLAN,
  BUTTON_UPGRADE_PLAN_2,
  LABEL_CURRENT_SUBSCRIPTION_PLAN,
  ADS_SUBSCRIPTION_BENEFIT_USERS,
  ADS_SUBSCRIPTION_BENEFIT_USER,
  ADS_SUBSCRIPTION_BENEFIT_USERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_UNLIMITED_SOCIAL_POSTS,
  ADS_SUBSCRIPTION_BENEFIT_SUPPORT_CHAT_AND_EMAIL,
  ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL,
  ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
  ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
  BUTTON_WATCH_DEMO,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import Button from "../Buttons/Button";
import {
  LINK_LANDING_CONTACT,
  PLAN_TYPE_PACKAGES,
  RECURRENT_MULTIPLIER,
} from "../../utils/constants";
import { BLACK_COLOR_BORDER, INPUT_COLOR_BORDER } from "../../utils/colors";
import { LucideCheck } from "lucide-react";
import { openLink } from "../../utils/string";
import { useState } from "react";
import {
  applyPromotionalDiscountToPrice,
  appendPrefilledPromoCodeToCheckoutUrl,
} from "../../utils/promotionalCodeCheckout";
import { useTheme } from "@mui/material";
import { useSelectorApp } from "../../lib/redux";
import Skeleton from "@mui/material/Skeleton";
import WhiteLabelPricingBenefitAction from "./WhiteLabelPricingBenefitAction";

export const PRICING_CARD_WIDTH = 280;

const TRANSLATIONS_WITHOUT_RECURRENT_MULTIPLIER = [
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISER,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_USERS,
  ADS_SUBSCRIPTION_BENEFIT_USER,
  ADS_SUBSCRIPTION_BENEFIT_USERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_UNLIMITED_SOCIAL_POSTS,
  ADS_SUBSCRIPTION_BENEFIT_SUPPORT_CHAT_AND_EMAIL,
  ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL,
  ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
  ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
];

// Component for Enterprise price
const EnterprisePrice = ({ t }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        textAlign: "center",
        fontWeight: 500,
        fontSize: "18px",
      }}
    >
      {t(CUSTOM)}
    </Typography>
  </Box>
);

// Component for price skeleton
const PriceSkeleton = ({ recurrent }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    <Skeleton variant="text" width={100} height={28} />
    {recurrent === "yearly" && (
      <Skeleton variant="text" width={120} height={20} />
    )}
  </Box>
);

// Component for prorated price
const ProratedPrice = ({
  proratedPrice,
  proratedCurrency,
  realPlanPrice,
  renewDate,
  t,
}) => {
  const proratedPriceText = t(VALUE_SUBSCRIPTION_PRORATED, {
    value: proratedPrice,
    currency: proratedCurrency,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          mb: 0.5,
          fontSize: "18px",
        }}
      >
        {proratedPriceText}
      </Typography>
      {realPlanPrice && renewDate && (
        <Typography
          sx={{
            fontSize: "14px",
          }}
        >
          {t(VALUE_SUBSCRIPTION_AFTER_PRORATED, {
            value: realPlanPrice,
            currency: proratedCurrency,
            date: new Date(renewDate),
          })}
        </Typography>
      )}
    </Box>
  );
};

// Component for yearly price
const YearlyPrice = ({ price, t }) => {
  const priceFormatted = Math.floor(price / 12);
  const monthlyPrice = t(VALUE_SUBSCRIPTION_MONTHLY, {
    value: priceFormatted,
    currency: "USD",
  });
  const yearlyPrice = t(VALUE_SUBSCRIPTION_YEARLY_UNIQUE_PAYMENT, {
    value: price,
    currency: "USD",
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          mb: 0.5,
          fontSize: "18px",
        }}
      >
        {monthlyPrice}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
        }}
      >
        {yearlyPrice}
      </Typography>
    </Box>
  );
};

const DiscountedPlanPrice = ({ basePrice, discountedPrice, recurrent, t }) => {
  const showYearly = recurrent === "yearly";
  const baseFormatted = showYearly ? Math.floor(basePrice / 12) : basePrice;
  const discountedFormatted = showYearly
    ? Math.floor(discountedPrice / 12)
    : discountedPrice;

  const originalMonthly = t(VALUE_SUBSCRIPTION_MONTHLY, {
    value: baseFormatted,
    currency: "USD",
  });
  const discountedMonthly = t(VALUE_SUBSCRIPTION_MONTHLY, {
    value: discountedFormatted,
    currency: "USD",
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textDecoration: "line-through",
          color: "text.secondary",
        }}
      >
        {originalMonthly}
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "18px",
        }}
      >
        {discountedMonthly}
      </Typography>
      {showYearly ? (
        <Typography sx={{ fontSize: "14px" }}>
          {t(VALUE_SUBSCRIPTION_YEARLY_UNIQUE_PAYMENT, {
            value: discountedPrice,
            currency: "USD",
          })}
        </Typography>
      ) : null}
    </Box>
  );
};

// Component for monthly price
const MonthlyPrice = ({ price, t }) => {
  const monthlyPrice = t(VALUE_SUBSCRIPTION_MONTHLY, {
    value: price,
    currency: "USD",
  });

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Typography
        sx={{
          textAlign: "center",
          fontWeight: 500,
          fontSize: "18px",
        }}
      >
        {monthlyPrice}
      </Typography>
    </Box>
  );
};

const PricingCard = ({
  data,
  recurrent,
  isUpgrade,
  variant = "default",
  onUpgradeSubscription = () => {},
  featuredPlanID = null,
  subscriptionTierRelation = null,
  onOpenBookDemo = () => {},
  checkoutPromo = null,
  isCheckoutPromoLoading = false,
}) => {
  const { t } = useTranslationApp();
  const [isLoading, setIsLoading] = useState(false);
  const {
    typeOfPlans,
    prorationPrices,
    isLoadingProrationPrices,
    plans: allPlans,
    subscriptionPlanInfo,
    subscriptionPlan,
  } = useSelectorApp((state) => state.payments);
  const planType = typeOfPlans || PLAN_TYPE_PACKAGES;

  const theme = useTheme();
  const isCompact = variant === "compact";

  const isFeaturedPlan = Boolean(featuredPlanID) && data.id === featuredPlanID;
  const isEnterprise = data.id === "enterprise";

  const getInformationPlan = () => {
    if (isUpgrade && prorationPrices && prorationPrices[data.id]) {
      return {
        price: prorationPrices[data.id].price,
        currency: prorationPrices[data.id].currency,
      };
    }
    const informationByType = data[recurrent]?.[planType];
    const information = informationByType || data[recurrent];
    return information || {};
  };
  const { price } = getInformationPlan();

  const promoMetadata =
    checkoutPromo && !isUpgrade
      ? {
          discount: checkoutPromo.discount,
          discountType: checkoutPromo.discountType,
        }
      : null;

  const discountedPrice =
    price && promoMetadata
      ? applyPromotionalDiscountToPrice(price, promoMetadata)
      : null;

  const showDiscountedPrice =
    Boolean(discountedPrice) &&
    Number.isFinite(Number(price)) &&
    discountedPrice < price;

  const getMainValueCard = () => {
    // Enterprise plan
    if (isEnterprise) {
      return <EnterprisePrice t={t} />;
    }

    // Show skeleton if loading proration prices or no price available
    const isLoadingProration =
      isUpgrade && isLoadingProrationPrices && !prorationPrices?.[data.id];

    if (isLoadingProration || isCheckoutPromoLoading || !price) {
      return <PriceSkeleton recurrent={recurrent} />;
    }

    if (showDiscountedPrice) {
      return (
        <DiscountedPlanPrice
          basePrice={price}
          discountedPrice={discountedPrice}
          recurrent={recurrent}
          t={t}
        />
      );
    }

    // Show prorated price on upgrade (credits or packages billing)
    const isProratedPrice = isUpgrade && prorationPrices?.[data.id];

    if (isProratedPrice) {
      const proratedPrice = prorationPrices[data.id].price;
      const proratedCurrency = prorationPrices[data.id].currency || "USD";
      const realPlanPrice =
        allPlans?.[data.id]?.[recurrent]?.[planType]?.price ||
        allPlans?.[data.id]?.[recurrent]?.credits?.price ||
        allPlans?.[data.id]?.[recurrent]?.price;
      const renewDate = subscriptionPlanInfo?.renewDate;

      return (
        <ProratedPrice
          proratedPrice={proratedPrice}
          proratedCurrency={proratedCurrency}
          realPlanPrice={realPlanPrice}
          renewDate={renewDate}
          t={t}
        />
      );
    }

    // Yearly or monthly price
    return recurrent === "yearly" ? (
      <YearlyPrice price={price} t={t} />
    ) : (
      <MonthlyPrice price={price} t={t} />
    );
  };

  const onGetButtonConfiguration = () => {
    if (isEnterprise) {
      return {
        text: t(BUTTON_CONTACT_US),
        onClick: () => {
          openLink(LINK_LANDING_CONTACT);
        },
        disabled: false,
      };
    }

    if (isUpgrade) {
      return {
        text: t(isCompact ? BUTTON_UPGRADE_PLAN_2 : BUTTON_UPGRADE_PLAN, {
          value: data.name,
        }),
        onClick: async () => {
          setIsLoading(true);
          await onUpgradeSubscription({ planID: data.id, paymentIntent: null });
          setIsLoading(false);
        },
        disabled: false,
      };
    }

    const paymentLinkUrl = data[recurrent]?.[planType]?.paymentLinkUrl;
    const checkoutUrl = checkoutPromo?.stripeCode
      ? appendPrefilledPromoCodeToCheckoutUrl(
          paymentLinkUrl,
          checkoutPromo.stripeCode
        )
      : paymentLinkUrl;

    return {
      text: t(BUTTON_SUBSCRIBE_NOW),
      onClick: () => {
        setIsLoading(true);
        window.location.href = checkoutUrl;
      },
      disabled: false,
    };
  };

  const showCtaButton =
    subscriptionTierRelation === null || subscriptionTierRelation === "above";

  const buttonConfig = showCtaButton ? onGetButtonConfiguration() : null;
  const benefitsToRender = isCompact
    ? data.benefits.filter(
        (benefit) =>
          benefit.translation !==
            ADS_SUBSCRIPTION_BENEFIT_UNLIMITED_SOCIAL_POSTS &&
          benefit.translation !== ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL
      )
    : data.benefits;

  return (
    <>
      <Box
        sx={{
          position: "relative",
          minWidth: isCompact ? 0 : PRICING_CARD_WIDTH,
          maxWidth: isCompact ? "none" : PRICING_CARD_WIDTH,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          ...(isCompact && { alignSelf: "stretch" }),
          mx: {
            xs: "auto",
          },
          mt: isCompact
            ? 0
            : {
                xs: isFeaturedPlan ? 1 : 0,
                sm: 0,
              },
        }}
      >
        {isFeaturedPlan && (
          <Box
            sx={{
              position: "absolute",
              top: -15,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              background: theme.palette.button.primary.main,
              color: theme.palette.button.primary.contrastText,
              borderRadius: "10px",
              px: 2,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <StarIcon sx={{ color: "white !important", fontSize: "18px" }} />
            <Typography
              sx={{
                color: "white !important",
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {t(MOST_POPULAR)}
            </Typography>
          </Box>
        )}
        <Card
          sx={{
            backgroundColor: "background.paper",
            border: isFeaturedPlan
              ? `${isCompact ? "1.5px" : "1px"} solid ${BLACK_COLOR_BORDER}`
              : `1px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: 3,
            width: "100%",
            height: isCompact ? "auto" : "100%",
            flex: isCompact ? "1 1 auto" : undefined,
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            boxShadow: isFeaturedPlan
              ? "0px 8px 24px rgba(0,0,0,0.15)"
              : "0px 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <CardHeader
            title={data.name}
            subheader={getMainValueCard()}
            titleTypographyProps={{
              mt: isCompact ? 1 : 1.5,
              align: "center",
              fontWeight: isCompact ? 700 : 600,
              fontStyle: isCompact ? "normal" : "italic",
              fontSize: isCompact ? "18px" : "20px",
            }}
            subheaderTypographyProps={{
              align: "center",
              color: "background.paperContrastText",
              fontWeight: 500,
              mb: 0.5,
              fontSize: "1.2rem",
            }}
            sx={{
              background: "background.paper",
              color: "background.paperContrastText",
              minHeight: isCompact ? "auto" : 88,
              ...(isCompact && {
                px: 1,
                pt: isFeaturedPlan ? 2.5 : 1,
              }),
            }}
          />
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "stretch",
              py: 0,
            }}
          >
            <Box
              sx={{
                pb: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {benefitsToRender.map((benefit, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <LucideCheck
                    size={18}
                    color="#4CAF50"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      align="left"
                      sx={{
                        "&::before": {
                          display: "none",
                        },
                        lineHeight: 1.4,
                        fontSize: isCompact ? "13px" : "14px",
                      }}
                    >
                      {t(benefit.translation, {
                        value:
                          TRANSLATIONS_WITHOUT_RECURRENT_MULTIPLIER.includes(
                            benefit.translation
                          )
                            ? benefit.value
                            : benefit.value * RECURRENT_MULTIPLIER[recurrent],
                      })}
                    </Typography>
                    {benefit.translation ===
                    ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL ? (
                      <WhiteLabelPricingBenefitAction />
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
          <CardActions
            sx={{
              p: 2,
              pt: 0,
              width: "100%",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              alignItems: "stretch",
            }}
          >
            {subscriptionTierRelation === "below" && (
              <Box sx={{ minHeight: 36.5, width: "100%" }} aria-hidden />
            )}
            {subscriptionTierRelation === "current" && (
              <Button
                fullWidth
                disabled
                variant={isCompact ? "outlined" : "contained"}
                color={
                  isCompact
                    ? "secondary"
                    : isFeaturedPlan
                      ? "primary"
                      : "secondary"
                }
                sx={{
                  ml: "0px !important",
                  width: "100%",
                  ...(isCompact && { minWidth: 0, px: 1 }),
                }}
              >
                {t(LABEL_CURRENT_SUBSCRIPTION_PLAN)}
              </Button>
            )}
            {showCtaButton && buttonConfig && (
              <Button
                fullWidth
                color="primary"
                loading={isLoading}
                onClick={buttonConfig.onClick}
                sx={{
                  ml: "0px !important",
                  width: "100%",
                  ...(isCompact && { minWidth: 0, px: 1 }),
                }}
              >
                {buttonConfig.text}
              </Button>
            )}
            {!subscriptionPlan && !isCompact && (
              <Button
                fullWidth
                color="secondary"
                onClick={onOpenBookDemo}
                sx={{
                  ml: "0px !important",
                  width: "100%",
                }}
              >
                {t(BUTTON_WATCH_DEMO)}
              </Button>
            )}
          </CardActions>
        </Card>
      </Box>
    </>
  );
};

export default PricingCard;
